import React, { useState, useEffect, useMemo } from 'react';
import { Users, Plus, Loader2 as Loader, Search, X } from 'lucide-react';
import { projectService } from '../services/projectService';
import { projectService as towerProjectService } from '../services/projectService';
import { verveContactService } from '@/services/VerveContactService';
import { showSuccessMessage, showInfoMessage } from '@/lib/toast';
import { Button } from '@/components/ui/Button';
import { FormField, Textarea } from '@/components/ui/FormComponents';
import { DataTable } from '@/components/ui/DataTable';

const PM_COLUMNS = [
   {
      header: 'Project Manager',
      key: 'projectManager',
      render: (row) => (
         <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded bg-blue-50 text-[#2b448f] font-semibold text-[12px] flex items-center justify-center border border-blue-100">
               {row.getInitials(row.pm.name || `${row.pm.firstName || ''} ${row.pm.lastName || ''}`.trim() || 'M')}
            </div>
            <span className="text-[13px] font-medium text-slate-700 font-['Poppins']">
               {row.pm.name || `${row.pm.firstName || ''} ${row.pm.lastName || ''}`.trim() || 'Manager'}
            </span>
         </div>
      )
   },
   {
      header: 'Contact Details',
      key: 'contactDetails',
      render: (row) => (
         <div className="flex flex-col">
            <span className="text-[12px] font-medium text-slate-600">{row.pm.email || '—'}</span>
            <span className="text-[12px] font-medium text-slate-600">{row.pm.phone || '—'}</span>
         </div>
      )
   },
   {
      header: 'Assignment Notes',
      key: 'assignmentNotes',
      className: 'max-w-[300px]',
      render: (row) => (
         <p className="text-[12px] font-medium text-slate-500 leading-relaxed italic truncate">
            {row.pmAssignmentNotes || 'No notes provided.'}
         </p>
      )
   }
];

export { ProjectManagerAssignmentForm } from './assign_project_manager';

export const ProjectManagerAssignment = ({ project, onUpdate, getInitials, onAssignClick }) => {
   const [managers, setManagers] = useState([]);

   useEffect(() => {
      const fetchManagers = async () => {
         try {
            const response = await towerProjectService.getProjectManagers();
            const rawManagers = Array.isArray(response) ? response : (response?.data?.data || response?.data || []);
            const activeManagers = rawManagers.filter((u) => u?.status !== 'Inactive');
            setManagers(activeManagers);
            
            // Auto-assign if exactly one manager exists and none is assigned
            const isUnassigned = !project?.projectManager || (Array.isArray(project.projectManager) && project.projectManager.length === 0);
            if (isUnassigned && activeManagers.length === 1) {
               const onlyManager = activeManagers[0];
               await verveContactService.updateContact(project._id, { projectManager: [onlyManager._id] });
               if (onUpdate) onUpdate();
            }
         } catch (err) {
            console.error(err);
         }
      };
      fetchManagers();
   }, [project?._id, project?.projectManager]);
   const actualProject = project?.data?.data || project?.data || project;

   console.log("PROJECT MANAGER DATA:", actualProject?.projectManager);

   const tableData = useMemo(() => {
      if (!actualProject?.projectManager) return [];
      const pms = Array.isArray(actualProject.projectManager) ? actualProject.projectManager : [actualProject.projectManager];
      // Filter out empty or invalid PMs
      const validPms = pms.filter(pm => pm && (typeof pm === 'string' ? pm.length > 0 : pm._id));
      if (validPms.length === 0) return [];
      
      return validPms.map(pm => ({
         pm: typeof pm === 'string' ? { _id: pm, name: 'Unknown Manager', email: '', phone: '' } : pm,
         pmAssignmentNotes: actualProject.pmAssignmentNotes,
         getInitials
      }));
   }, [actualProject?.projectManager, actualProject?.pmAssignmentNotes, getInitials]);

   return (
      <div className="bg-white border border-slate-100 rounded-xl flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom duration-500">
         <div className="p-4 border-b border-slate-100 flex items-center justify-end">
            <button 
               onClick={onAssignClick} 
               className="flex items-center gap-2 px-4 py-2 bg-transparent border border-[#2b448f] cursor-pointer text-[#2b448f] rounded-md text-[10px] font-semibold tracking-wider hover:bg-blue-50 transition-all shadow-sm"
            >
               <Plus className="w-3.5 h-3.5" />
               {(!actualProject?.projectManager || (Array.isArray(actualProject.projectManager) && actualProject.projectManager.length === 0)) ? 'Assign PM' : 'Update PM'}
            </button>
         </div>

         <DataTable
            columns={PM_COLUMNS}
            data={tableData}
            noScroll={true}
         />
      </div>
   );
};
